import React, { useState } from 'react';
import { X, Check } from 'lucide-react';
import { RyanSubdeckGroupData } from '../types/ryan.types';
import { getRyanIconComponent } from '../utils/ryanGroupIcons';

interface SubdeckData {
  _id: string;
  name: string;
  groupId?: string | null;
}

interface RyanAssignGroupModalProps {
  spaceId: string;
  subdeck: SubdeckData;
  groups: RyanSubdeckGroupData[];
  activeTabId: string;
  onClose: () => void;
  onSuccess: () => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const RyanAssignGroupModal: React.FC<RyanAssignGroupModalProps> = ({
  spaceId, subdeck, groups, activeTabId, onClose, onSuccess, onToast,
}) => {
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(subdeck.groupId || null);
  const [saving, setSaving] = useState(false);

  const tabGroups = groups.filter(g => {
    if (!activeTabId || activeTabId === '__no_tab__') return !g.tabId;
    return g.tabId === activeTabId;
  });

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeck._id}/group`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ groupId: selectedGroupId }),
      });
      if (!res.ok) {
        onToast('Erro ao atribuir grupo', 'error');
        return;
      }
      onToast('Grupo atribuído!', 'success');
      onSuccess();
      onClose();
    } catch {
      onToast('Erro de conexão', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ryan-modal-overlay" onClick={onClose}>
      <div className="ryan-modal" onClick={e => e.stopPropagation()}>
        <div className="ryan-modal-header">
          <h3 className="ryan-modal-title">Mover para Grupo</h3>
          <button className="ryan-modal-close-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="ryan-modal-body">
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', marginBottom: 14 }}>
            Deck: <strong style={{ color: '#0D3B0E' }}>{subdeck.name}</strong>
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Opção: sem grupo */}
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderRadius: 10,
                border: `2px solid ${selectedGroupId === null ? '#0D3B0E' : 'rgba(0,0,0,0.08)'}`,
                background: selectedGroupId === null ? 'rgba(13,59,14,0.05)' : '#f8faf8',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 150ms',
              }}
              onClick={() => setSelectedGroupId(null)}
            >
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={16} color="#9CA3AF" />
              </div>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#444', fontFamily: 'Inter, sans-serif' }}>
                Sem grupo
              </span>
              {selectedGroupId === null && (
                <Check size={16} color="#0D3B0E" style={{ marginLeft: 'auto' }} />
              )}
            </button>

            {tabGroups.map(group => {
              const IconComp = getRyanIconComponent(group.iconName);
              const isSelected = selectedGroupId === group._id;
              return (
                <button
                  key={group._id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: `2px solid ${isSelected ? group.color : 'rgba(0,0,0,0.08)'}`,
                    background: isSelected ? `${group.color}0d` : '#f8faf8',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 150ms',
                  }}
                  onClick={() => setSelectedGroupId(group._id)}
                >
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: `${group.color}18`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <IconComp size={16} color={group.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111', fontFamily: 'Inter, sans-serif' }}>
                      {group.name}
                    </div>
                    {group.description && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>
                        {group.description}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <Check size={16} color={group.color} style={{ flexShrink: 0 }} />
                  )}
                </button>
              );
            })}

            {tabGroups.length === 0 && (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: '16px 0', fontFamily: 'Inter, sans-serif' }}>
                Nenhum grupo criado para esta aba.
              </p>
            )}
          </div>
        </div>

        <div className="ryan-modal-footer">
          <button className="ryan-modal-cancel-btn" onClick={onClose}>Cancelar</button>
          <button className="ryan-modal-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RyanAssignGroupModal;
